/**
 * One channel.
 *
 * Built to the current WhatsApp channel screen as described by its own help
 * pages and the reference shots in docs/references: the channel's name and
 * follower count in the header, Follow at the top right, a bell for mute
 * beside it, and the posts below as a single column of left-aligned cards.
 *
 * Why the posts are not chat bubbles, despite looking a little like them:
 * there is only one speaker, so a left-and-right split would be meaningless,
 * and every post carries a reaction bar and a view count that a bubble has
 * nowhere to put. The composer only appears if you may post.
 *
 * The reaction bar shows counts and no names, because the server stores no
 * names. That is the channel privacy promise and it goes all the way down —
 * see docs/CHANNELS.md.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  channels as channelService, formatTime, formatDayDivider, isSameDay, followerLabel
} from '@zivapp/shared'
import Avatar from '../components/ui/Avatar.jsx'
import './Channel.css'

/**
 * The reactions offered on a post.
 *
 * A fixed set rather than a full picker: a channel reaction is a tally in a
 * shared row, not a message of your own, and an open picker turns a tidy bar
 * into a list of one-offs nobody can read at a glance.
 */
const REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏']

function ViewCount ({ count }) {
  return (
    <span className="ch__views" title={`${count} view${count === 1 ? '' : 's'}`}>
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M2.5 12S6 5.8 12 5.8 21.5 12 21.5 12 18 18.2 12 18.2 2.5 12 2.5 12Z"
          stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        <circle cx="12" cy="12" r="2.8" stroke="currentColor" strokeWidth="1.6" />
      </svg>
      {count}
    </span>
  )
}

function Post ({ post, canManage, onReact, onDelete }) {
  const [picking, setPicking] = useState(false)
  const tallies = Object.entries(post.reactions).filter(([, n]) => n > 0)

  const react = emoji => {
    setPicking(false)
    onReact(post, emoji)
  }

  return (
    <article className="ch__post">
      <div className="ch__post-body">
        {post.mediaUrl && (
          <img className="ch__post-media" src={post.mediaUrl} alt="" />
        )}
        {post.body && <p className="ch__post-text">{post.body}</p>}

        <div className="ch__post-meta">
          {post.editedAt && <span className="ch__edited">Edited</span>}
          <ViewCount count={post.viewCount} />
          <span className="ch__post-time">{formatTime(post.createdAt)}</span>
        </div>
      </div>

      <div className="ch__post-bar">
        {tallies.map(([emoji, count]) => (
          <button
            key={emoji}
            type="button"
            className="ch__tally"
            onClick={() => react(emoji)}
          >
            <span aria-hidden="true">{emoji}</span> {count}
          </button>
        ))}

        <button
          type="button"
          className="ch__react"
          onClick={() => setPicking(open => !open)}
          aria-label="React to this post"
          aria-expanded={picking}
        >
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="8.6" stroke="currentColor" strokeWidth="1.6" />
            <circle cx="9.3" cy="10" r="1.1" fill="currentColor" />
            <circle cx="14.7" cy="10" r="1.1" fill="currentColor" />
            <path d="M8.6 14.2a4.2 4.2 0 0 0 6.8 0" stroke="currentColor" strokeWidth="1.6"
              strokeLinecap="round" />
          </svg>
        </button>

        {canManage && (
          <button
            type="button"
            className="ch__react ch__react--danger"
            onClick={() => onDelete(post)}
            aria-label="Delete this post"
          >
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M5 7h14M10 7V5.4A1.4 1.4 0 0 1 11.4 4h1.2A1.4 1.4 0 0 1 14 5.4V7M7 7l.8 12.1a1.4 1.4 0 0 0 1.4 1.3h5.6a1.4 1.4 0 0 0 1.4-1.3L17 7"
                stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}

        {picking && (
          <div className="ch__picker" role="group" aria-label="Choose a reaction">
            {REACTIONS.map(emoji => (
              <button key={emoji} type="button" className="ch__picker-btn"
                onClick={() => react(emoji)}>
                {emoji}
              </button>
            ))}
          </div>
        )}
      </div>
    </article>
  )
}

export default function Channel () {
  const { channelId } = useParams()
  const navigate = useNavigate()

  const [channel, setChannel] = useState(null)
  const [posts, setPosts] = useState([])
  const [isLoading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  const endRef = useRef(null)

  const refresh = useCallback(async () => {
    try {
      setChannel(await channelService.get(channelId))
      setError(null)
    } catch (cause) {
      setError(cause)
    } finally {
      setLoading(false)
    }
  }, [channelId])

  useEffect(() => { refresh() }, [refresh])
  useEffect(() => channelService.subscribeToPosts(channelId, setPosts), [channelId])

  // Clearing the badge is a side effect of arriving, not of a button. Opening
  // the channel IS the read.
  useEffect(() => {
    if (channel?.isFollowing) channelService.markRead(channelId).catch(() => {})
  }, [channelId, channel?.isFollowing])

  const firstPaint = useRef(true)
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: firstPaint.current ? 'auto' : 'smooth' })
    firstPaint.current = false
  }, [posts.length, channelId])

  const toggleFollow = async () => {
    if (channel.isFollowing) {
      await channelService.unfollow(channelId)
    } else {
      await channelService.follow(channelId)
    }
    refresh()
  }

  const toggleMute = async () => {
    await channelService.setMuted(channelId, !channel.isMuted)
    refresh()
  }

  const publish = async event => {
    event.preventDefault()
    const body = draft.trim()
    if (!body || sending) return

    setSending(true)
    try {
      await channelService.post(channelId, { body })
      setDraft('')
    } finally {
      setSending(false)
    }
  }

  const react = async (post, emoji) => {
    // Patched in place rather than refetched: the subscription will bring the
    // authoritative tally along in a moment, and waiting for a round trip to
    // acknowledge a tap is what makes a reaction feel broken.
    setPosts(list => list.map(p => p.id === post.id
      ? { ...p, reactions: { ...p.reactions, [emoji]: (p.reactions[emoji] ?? 0) + 1 } }
      : p))
    try {
      await channelService.react(post.id, emoji)
    } catch {
      setPosts(list => list.map(p => p.id === post.id ? post : p))
    }
  }

  const removePost = async post => {
    await channelService.deletePost(post.id)
  }

  if (isLoading) {
    return <div className="ch ch--message">Loading channel…</div>
  }

  if (error || !channel) {
    return (
      <div className="ch ch--message">
        <p>This channel is no longer available.</p>
        <Link className="ch__back-link" to="/updates">Back to Updates</Link>
      </div>
    )
  }

  return (
    <div className="ch">
      <header className="ch__bar">
        <button
          type="button"
          className="ch__icon-btn ch__back"
          onClick={() => navigate('/updates')}
          aria-label="Back"
        >
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M15 5 8 12l7 7" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        <Avatar name={channel.name} url={channel.avatarUrl} size={40} />

        <div className="ch__ident">
          <span className="ch__name">{channel.name}</span>
          <span className="ch__followers">{followerLabel(channel.followerCount)}</span>
        </div>

        {channel.isFollowing && (
          <button
            type="button"
            className={`ch__icon-btn${channel.isMuted ? ' is-on' : ''}`}
            onClick={toggleMute}
            aria-label={channel.isMuted ? 'Unmute this channel' : 'Mute this channel'}
            aria-pressed={channel.isMuted}
          >
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M6 9.5a6 6 0 0 1 12 0c0 4 1.3 5.4 1.9 6.1a.6.6 0 0 1-.4 1H4.5a.6.6 0 0 1-.4-1C4.7 14.9 6 13.5 6 9.5Z"
                stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
              <path d="M10 19.3a2.3 2.3 0 0 0 4 0" stroke="currentColor" strokeWidth="1.7"
                strokeLinecap="round" />
              {channel.isMuted && (
                <path d="M4 4l16 16" stroke="currentColor" strokeWidth="1.9"
                  strokeLinecap="round" />
              )}
            </svg>
          </button>
        )}

        <button
          type="button"
          className={`ch__follow${channel.isFollowing ? ' is-following' : ''}`}
          onClick={toggleFollow}
          data-testid="follow-toggle"
        >
          {channel.isFollowing ? 'Following' : 'Follow'}
        </button>
      </header>

      {channel.description && (
        <p className="ch__description">{channel.description}</p>
      )}

      <div className="ch__scroll">
        {posts.length === 0 && (
          <p className="ch__empty">
            {channel.isAdmin
              ? 'Nothing posted yet. Whatever you write below reaches every follower.'
              : 'This channel has not posted anything yet.'}
          </p>
        )}

        {posts.map((post, i) => {
          const previous = posts[i - 1]
          const newDay = !previous || !isSameDay(previous.createdAt, post.createdAt)
          return (
            <div key={post.id}>
              {newDay && (
                <div className="ch__day">{formatDayDivider(post.createdAt)}</div>
              )}
              <Post
                post={post}
                canManage={channel.isAdmin}
                onReact={react}
                onDelete={removePost}
              />
            </div>
          )
        })}
        <div ref={endRef} />
      </div>

      {channel.isAdmin ? (
        <form className="ch__composer" onSubmit={publish}>
          <input
            className="ch__input"
            placeholder="Post an update"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            aria-label="Post an update"
          />
          <button
            type="submit"
            className="ch__send"
            disabled={!draft.trim() || sending}
            aria-label="Post"
            data-testid="channel-send"
          >
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M3.4 11.2 20 4l-7.2 16.6-2-6.6-7.4-2.8Z" stroke="currentColor"
                strokeWidth="1.7" strokeLinejoin="round" />
            </svg>
          </button>
        </form>
      ) : (
        /* Not a disabled composer. A channel is one-way, and an input you
           cannot type into invites people to try; a sentence explains it. */
        <p className="ch__readonly">Only admins can post to this channel.</p>
      )}
    </div>
  )
}
